export const loadImage = (src) => new Promise((res, rej) => {
  const i = new Image();
  i.onload = () => res(i);
  i.onerror = () => rej(new Error("Could not read this image"));
  i.src = src;
});

export const newCanvas = (w, h) => { const c = document.createElement("canvas"); c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h)); return c; };

export const toCanvas = (src) => { const c = newCanvas(src.naturalWidth || src.width, src.naturalHeight || src.height); c.getContext("2d").drawImage(src, 0, 0); return c; };

export const cloneCanvas = (c) => toCanvas(c);

export const rotateCanvas = (src, deg) => {
  if (!deg) return src;
  const r = (deg * Math.PI) / 180, s = Math.abs(Math.sin(r)), co = Math.abs(Math.cos(r));
  const w = src.width * co + src.height * s, h = src.width * s + src.height * co;
  const c = newCanvas(w, h), x = c.getContext("2d");
  x.imageSmoothingQuality = "high";
  x.translate(c.width / 2, c.height / 2); x.rotate(r); x.drawImage(src, -src.width / 2, -src.height / 2);
  return c;
};

export const alphaBBox = (c, threshold = 8) => {
  const { width: w, height: h } = c, d = c.getContext("2d").getImageData(0, 0, w, h).data;
  let x0 = w, y0 = h, x1 = -1, y1 = -1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    if (d[(y * w + x) * 4 + 3] > threshold) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
};

export const hasTransparency = (c) => {
  const d = c.getContext("2d").getImageData(0, 0, c.width, c.height).data;
  for (let i = 3; i < d.length; i += 16) if (d[i] < 250) return true;
  return false;
};

export const cropCanvas = (src, r) => {
  const c = newCanvas(r.w, r.h);
  c.getContext("2d").drawImage(src, r.x, r.y, r.w, r.h, 0, 0, c.width, c.height);
  return c;
};

export const padCanvas = (src, pct) => {
  if (!pct) return src;
  const p = Math.round(Math.max(src.width, src.height) * (pct / 100));
  const c = newCanvas(src.width + p * 2, src.height + p * 2);
  c.getContext("2d").drawImage(src, p, p);
  return c;
};

export const fitCanvas = (src, w, h) => {
  if (!w && !h) return src;
  const tw = w || Math.round((h / src.height) * src.width), th = h || Math.round((w / src.width) * src.height);
  const k = Math.min(tw / src.width, th / src.height);
  const c = newCanvas(tw, th), x = c.getContext("2d");
  x.imageSmoothingQuality = "high";
  const dw = src.width * k, dh = src.height * k;
  x.drawImage(src, (tw - dw) / 2, (th - dh) / 2, dw, dh);
  return c;
};

export const BG_PRESETS = { white: "#FFFFFF", black: "#05090B", turquoise: "#00E5BC" };

export const composeBackground = (src, bg) => {
  if (!bg || bg.type === "transparent") return src;
  const c = newCanvas(src.width, src.height), x = c.getContext("2d");
  x.globalAlpha = (bg.opacity ?? 100) / 100;
  if (bg.type === "gradient") {
    const a = ((bg.angle ?? 135) * Math.PI) / 180, cx = c.width / 2, cy = c.height / 2, L = Math.hypot(c.width, c.height) / 2;
    const g = x.createLinearGradient(cx - Math.cos(a) * L, cy - Math.sin(a) * L, cx + Math.cos(a) * L, cy + Math.sin(a) * L);
    g.addColorStop(0, bg.color); g.addColorStop(1, bg.color2); x.fillStyle = g; x.fillRect(0, 0, c.width, c.height);
  } else if (bg.type === "image" && bg.image) {
    const k = Math.max(c.width / bg.image.width, c.height / bg.image.height), w = bg.image.width * k, h = bg.image.height * k;
    x.drawImage(bg.image, (c.width - w) / 2, (c.height - h) / 2, w, h);
  } else {
    x.fillStyle = bg.type === "solid" ? bg.color : BG_PRESETS[bg.type] || "#FFFFFF"; x.fillRect(0, 0, c.width, c.height);
  }
  x.globalAlpha = 1;
  x.drawImage(src, 0, 0);
  return c;
};

export const toBlob = (c, type = "image/png", q = 0.95) => new Promise((res, rej) => c.toBlob((b) => (b ? res(b) : rej(new Error("Export failed"))), type, q));

export const blobToDataUrl = (b) => new Promise((res) => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(b); });

export const DEFAULT_PARAMS = { mode: "standard", rotation: 0, straighten: 0, crop: null, padding: 4, outW: "", outH: "", bg: { type: "transparent", color: "#101718", color2: "#00E5BC", angle: 135, opacity: 100 } };

export function render(current, p, bgImage) {
  let c = rotateCanvas(current, (p.rotation || 0) + (p.straighten || 0));
  if (p.mode === "cutout") {
    const b = alphaBBox(c);
    if (b) c = padCanvas(cropCanvas(c, b), p.padding);
  } else if (p.crop && p.crop.width > 0 && p.crop.height > 0) {
    c = cropCanvas(c, { x: (p.crop.x / 100) * c.width, y: (p.crop.y / 100) * c.height, w: (p.crop.width / 100) * c.width, h: (p.crop.height / 100) * c.height });
  }
  c = fitCanvas(c, parseInt(p.outW, 10) || 0, parseInt(p.outH, 10) || 0);
  return composeBackground(c, { ...p.bg, image: bgImage });
}

export const fmtBytes = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(2)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export const download = (blob, name) => { const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); };
